import { Line } from 'react-chartjs-2';
import { useState, useEffect } from 'react';
import Dropdown from 'components/common/elements/Dropdown';
import styles from 'styles/components/pages/professor/statistics/LineChart.module.scss';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { getScoreColor } from 'utils/score';
import { TScore } from 'types/chart';
import { TSelectObject } from 'types/dropdown';

type TProps = {
  data: TScore[];
};

export const options = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    datalabels: {
      align: 'top' as const,
      anchor: 'end' as const,
      font: {
        size: 14,
      },
    },
    annotation: {
      annotations: [
        {
          mode: 'horizontal',
          scaleID: 'y',
          value: 50, // 가로선을 그릴 위치
          borderColor: STYLES_VARIABLES.GRAY_04,
          borderWidth: 1,
        },
      ],
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        font: {
          weight: 'bold',
        },
      },
    },
    y: {
      max: 110, // Y축 최대값 설정
      min: 0, // Y축 최소값 설정
      ticks: {
        stepSize: 50,
        callback: (value: any) => {
          // 눈금 렌더링 콜백 함수
          if (value === 0 || value === 50 || value === 100) {
            return value.toString(); // 0, 50, 100 눈금만 보이도록 설정
          }
          return '';
        },
      },
      grid: {
        display: true,
        color: STYLES_VARIABLES.GRAY_05,
        borderWidth: 1,
        drawBorder: false,
        drawOnChartArea: false,
        drawTicks: false,
      },
    },
  },
};

const LineChart = ({ data }: TProps) => {
  const [labels, setLabels] = useState<string[]>([]);
  const [lineData, setLineData] = useState<(number | null)[]>([]);
  const [roundList, setRoundList] = useState<TSelectObject[]>([]);
  const [startRound, setStartRound] = useState<TSelectObject>();
  const [endRound, setEndRound] = useState<TSelectObject>();
  const [showStartRoundList, setShowStartRoundList] = useState(false);
  const [showEndRoundList, setShowEndRoundList] = useState(false);

  useEffect(() => {
    const list = data.map((item) => ({
      label: `${item.label}회차`,
      value: item.label,
    }));
    const start = list[0];
    const end = list[list.length - 1];
    setRoundList(list || []);
    setStartRound(start || null);
    setEndRound(end || null);
    setData(Number(start), Number(end));
  }, [data]);

  useEffect(() => {
    if (data.length === 0) return;
    setData(Number(startRound?.value), Number(endRound?.value));
  }, [startRound, endRound]);

  const setData = (start: number | undefined, end: number | undefined) => {
    setLabels(() => {
      const list = data
        .filter((item) => {
          if (!start || !end) return item;
          return Number(item.label) >= start && Number(item.label) <= end;
        })
        .map((item) => `${item.label}회차`);
      list.unshift('');
      list.push('');
      return list;
    });
    setLineData(() => {
      const list = data
        .filter((item) => {
          if (!start || !end) return item;
          return Number(item.label) >= start && Number(item.label) <= end;
        })
        .map((item) => item.score);
      list.unshift(null);
      list.push(null);
      return list;
    });
  };

  const LineData = {
    labels,
    datasets: [
      {
        label: '',
        data: lineData,
        borderWidth: 1,
        borderColor: STYLES_VARIABLES.GRAY_06,
        pointStyle: 'circle',
        pointRadius: 6,
        pointHoverRadius: 6,
        pointBackgroundColor: lineData.map(
          (score) => STYLES_VARIABLES[getScoreColor(score)]
        ),
        pointBorderWidth: 0,
        datalabels: {
          color: lineData.map(
            (score) => STYLES_VARIABLES[getScoreColor(score)]
          ),
        },
      },
    ],
  };

  return (
    <div className={styles.section}>
      <div className="flex justify_between mb24">
        <div className={styles.title_wrapper}>
          <h2 className="main_title">성적 변화 추이</h2>
          <span className={styles.unit}> 단위 : (점)</span>
        </div>
        <div className="flex items_center">
          <Dropdown
            selectList={roundList}
            selectedObject={startRound}
            showSelectList={showStartRoundList}
            setShowSelectList={setShowStartRoundList}
            btnStyle={{
              width: '120px',
              height: '40px',
              backgroundColor: STYLES_VARIABLES.WHITE,
            }}
            dropdownStyle={{ width: '120px' }}
            placeholder="회차"
            onClickButton={() => setShowStartRoundList(!showStartRoundList)}
            onClickSelectItem={(selected) => {
              setShowStartRoundList(false);
              setStartRound(selected);
            }}
            defaultValue={startRound?.label}
          />
          <span
            className="ml16 mr16 fs16 fw700"
            style={{
              color:
                STYLES_VARIABLES[roundList.length > 0 ? 'GRAY_08' : 'GRAY_06'],
            }}
          >
            ~
          </span>
          <Dropdown
            selectList={roundList}
            selectedObject={endRound}
            showSelectList={showEndRoundList}
            setShowSelectList={setShowEndRoundList}
            btnStyle={{
              width: '120px',
              height: '40px',
              backgroundColor: STYLES_VARIABLES.WHITE,
            }}
            dropdownStyle={{ width: '120px' }}
            placeholder="회차"
            onClickButton={() => setShowEndRoundList(!showEndRoundList)}
            onClickSelectItem={(selected) => {
              setShowEndRoundList(false);
              setEndRound(selected);
            }}
            defaultValue={endRound?.label}
          />
        </div>
      </div>
      <div className={styles.wrapper}>
        {/* 라인 그래프 */}
        <Line width={972} height={320} data={LineData} options={options} />
      </div>
    </div>
  );
};

export default LineChart;
